"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function BlogGridConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div data-section-id="222328" className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2 min-h-[400px] rounded-xl bg-muted/50 animate-pulse" />
        <div className="min-h-[400px] rounded-xl bg-muted/50 animate-pulse" />
        {[...Array(3)].map((_, i) => <div key={i} className="h-[300px] rounded-xl bg-muted/50 animate-pulse" />)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-12">
        <p className="text-destructive font-default">Unable to load our latest stories at this time.</p>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="text-center py-12">
        <p className="text-muted-foreground font-default">No journal entries found.</p>
      </div>;
  }
  return <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {posts.map((post, i) => {
      const isFeatured = i === 0;
      if (isFeatured) {
        return <article key={post.id} data-index={i} className="md:col-span-2 relative overflow-hidden rounded-xl group min-h-[400px] md:min-h-[480px] flex items-end shadow-md transition-shadow duration-500 hover:shadow-xl">
              <img src={post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1645038/img/generic_blog_featured.png"} alt={post.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
              <div className="relative z-10 p-6 md:p-10 w-full">
                {post.categories && post.categories.length > 0 && <Badge variant="default" className="mb-4 bg-primary text-primary-foreground border-none font-default uppercase tracking-widest text-[10px]">
                    {post.categories[0].name}
                  </Badge>}
                <Link to={post.link || "#"} className="block">
                  <h3 className="font-default text-3xl md:text-5xl font-bold text-white mb-4 leading-tight group-hover:underline decoration-1 underline-offset-8">
                    {post.title}
                  </h3>
                </Link>
                <p className="text-white/90 line-clamp-2 max-w-2xl font-default text-base md:text-lg">
                  {post.excerpt || "CRUST is a boutique bakery that crafts artisan breads, pastries, and cakes using premium ingredients and time‑honored techniques. The brand highlights my baked goods, facilitates online orders, and engages your customers with mouth-watering visuals that showcase golden crusts, delicate fillings, and artistic decorations. Each item is designed to deliver fresh flavor and comforting nostalgia, appealing to local food lovers and special‑occasion shoppers. CRUST’s commitment to quality, community sourcing, and memorable taste positions it as a trusted destination for indulgent treats delivered straight to the door."}
                </p>
              </div>
            </article>;
      }
      return <article key={post.id} data-index={i} className="flex flex-col rounded-xl overflow-hidden bg-card border border-border/40 transition-all duration-500 hover:bg-accent/30 hover:shadow-lg group">
            <Link to={post.link || "#"} className="block aspect-[4/3] overflow-hidden">
              <img src={post.featuredImage || `/img/generic_blog_thumb_${i}.png`} alt={post.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
            </Link>
            <div className="p-6 flex flex-col flex-1">
              {post.categories && post.categories.length > 0 && <Badge variant="outline" className="mb-4 w-fit bg-secondary/30 border-border text-primary font-default text-[10px] uppercase tracking-wider">
                  {post.categories[0].name}
                </Badge>}
              <Link to={post.link || "#"} className="block mb-3">
                <h3 className="font-default text-xl md:text-2xl font-bold text-foreground leading-snug group-hover:text-primary transition-colors">
                  {post.title}
                </h3>
              </Link>
              <p className="text-muted-foreground text-sm line-clamp-3 mb-6 font-default flex-1 leading-relaxed">
                {post.excerpt || "CRUST is a boutique bakery that crafts artisan breads, pastries, and cakes using premium ingredients and time‑honored techniques. The brand highlights my baked goods, facilitates online orders, and engages your customers with mouth-watering visuals that showcase golden crusts, delicate fillings, and artistic decorations. Each item is designed to deliver fresh flavor and comforting nostalgia, appealing to local food lovers and special‑occasion shoppers. CRUST’s commitment to quality, community sourcing, and memorable taste positions it as a trusted destination for indulgent treats delivered straight to the door."}
              </p>
              <div className="mt-auto pt-4 border-t border-border/40 flex items-center justify-between">
                <span className="text-[10px] text-muted-foreground font-default uppercase tracking-widest font-semibold">
                  {post.date ? new Date(post.date).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              }) : 'Freshly Baked'}
                </span>
                <Link to={post.link || "#"} className="text-xs font-bold text-primary hover:text-primary/80 transition-colors font-default uppercase tracking-tighter">
                  Read Entry
                </Link>
              </div>
            </div>
          </article>;
    })}
    </div>;
}
export default function UltimasDoBlog() {
  const wp_query_params = {
    post_type: "post",
    per_page: 7,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="latest-from-the-kitchen" className="w-full bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="mb-12 md:mb-16 text-center md:text-left flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="max-w-2xl">
            <h2 className="font-default text-4xl md:text-6xl font-bold text-foreground mb-4 tracking-tight">
              Latest from the Kitchen
            </h2>
            <p className="font-default text-lg text-muted-foreground leading-relaxed">
              Stories of flour, fire, and the slow-rise process behind every CRUST creation.
            </p>
          </div>
          <div className="hidden md:block">
            <Link to="#">
              <Button variant="outline" className="font-default border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-full px-8">
                View Baker's Journal
              </Button>
            </Link>
          </div>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 7,
        order: "desc",
        orderby: "date"
      }}>
          <BlogGridConsumer />
        </PostsProvider>

        <div className="mt-10 text-center md:hidden">
          <Link to="#">
            <Button variant="outline" className="font-default w-full border-primary text-primary rounded-full">
              View Baker's Journal
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}